if (!customElements.get('product-grid')) {
  customElements.define(
    'product-grid',
    class ProductGrid extends HTMLElement {
      constructor() {
        super();

        this.isLoading = false;
        this.pendingVariantId = null;
        this.intersectionObserver = null;

        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
        this.handleQuickAddClick = this.handleQuickAddClick.bind(this);
        this.handleCartUpdated = this.handleCartUpdated.bind(this);
        this.handleCartError = this.handleCartError.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
      }

      connectedCallback() {
        this.sortSelect = this.querySelector('[data-sort-select]');
        this.productGrid = this.querySelector('[data-product-grid]');
        this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
        this.loadMoreButton = this.querySelector('[data-load-more]');

        this.sortSelect?.addEventListener('change', this.handleSortChange);
        this.loadMoreButton?.addEventListener('click', this.handleLoadMoreClick);
        this.addEventListener('click', this.handleQuickAddClick);
        document.addEventListener('cart:updated', this.handleCartUpdated);
        document.addEventListener('cart:error', this.handleCartError);

        this.setupAutoLoad();
      }

      disconnectedCallback() {
        this.sortSelect?.removeEventListener('change', this.handleSortChange);
        this.loadMoreButton?.removeEventListener('click', this.handleLoadMoreClick);
        this.removeEventListener('click', this.handleQuickAddClick);
        document.removeEventListener('cart:updated', this.handleCartUpdated);
        document.removeEventListener('cart:error', this.handleCartError);
        this.teardownAutoLoad();
      }

      get sectionId() {
        return this.dataset.sectionId || '';
      }

      get collectionUrl() {
        return this.dataset.collectionUrl || window.location.pathname;
      }

      get loadMoreType() {
        return this.dataset.loadMoreType || 'click';
      }

      get nextUrl() {
        return this.dataset.nextUrl || '';
      }

      set nextUrl(value) {
        if (value) {
          this.dataset.nextUrl = value;
        } else {
          delete this.dataset.nextUrl;
        }
      }

      get hasMore() {
        return this.dataset.hasMore === 'true';
      }

      set hasMore(value) {
        this.dataset.hasMore = value ? 'true' : 'false';
      }

      get sortBy() {
        return this.dataset.sortBy || this.sortSelect?.value || '';
      }

      set sortBy(value) {
        this.dataset.sortBy = value;
      }

      getAutoLoadTarget() {
        const cards = this.productGrid?.querySelectorAll('.product-card');

        if (cards?.length) {
          return cards[cards.length - 1];
        }

        return this.querySelector('[data-load-sentinel]');
      }

      setupAutoLoad() {
        this.teardownAutoLoad();

        if (this.loadMoreType !== 'auto' || !this.hasMore) return;

        const target = this.getAutoLoadTarget();

        if (!target) return;

        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
          rootMargin: '200px 0px',
        });
        this.intersectionObserver.observe(target);
      }

      teardownAutoLoad() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
      }

      handleIntersection(entries) {
        const isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible || this.isLoading || !this.hasMore) return;

        this.loadMore();
      }

      handleSortChange(event) {
        const sortBy = event.target.value;

        if (!sortBy || sortBy === this.sortBy) return;

        this.sortBy = sortBy;
        this.replaceGrid({ sortBy });
      }

      handleLoadMoreClick(event) {
        event.preventDefault();

        if (
          this.isLoading ||
          !this.hasMore ||
          this.loadMoreButton?.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.loadMore();
      }

      async loadMore() {
        if (!this.nextUrl) return;

        await this.fetchAndApply({
          url: this.nextUrl,
          mode: 'append',
        });
      }

      async replaceGrid({ sortBy }) {
        const url = new URL(this.collectionUrl, window.location.origin);

        if (sortBy) {
          url.searchParams.set('sort_by', sortBy);
        }

        await this.fetchAndApply({
          url: url.toString(),
          mode: 'replace',
        });
      }

      buildSectionUrl(url) {
        const sectionUrl = new URL(url, window.location.origin);
        sectionUrl.searchParams.set('section_id', this.sectionId);

        return sectionUrl;
      }

      buildPublicUrl({ sortBy }) {
        const url = new URL(this.collectionUrl, window.location.origin);

        if (sortBy) {
          url.searchParams.set('sort_by', sortBy);
        }

        return `${url.pathname}${url.search}`;
      }

      async fetchAndApply({ url, mode }) {
        if (this.isLoading || !this.sectionId || !url) return;

        this.isLoading = true;
        this.setLoadMoreLoading(true);

        try {
          const response = await fetch(this.buildSectionUrl(url).toString());

          if (!response.ok) {
            throw new Error(`Product grid request failed: ${response.status}`);
          }

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.querySelector('product-grid');

          if (!incoming) {
            throw new Error('Product grid markup missing from response');
          }

          if (mode === 'replace') {
            this.replaceFromIncoming(incoming);
            history.pushState({}, '', this.buildPublicUrl({ sortBy: this.sortBy }));
          } else {
            this.appendFromIncoming(incoming);
          }

          if (this.hasMore) {
            this.setupAutoLoad();
          } else {
            this.teardownAutoLoad();
          }
        } catch (error) {
          console.error('Product grid error:', error);
        } finally {
          this.isLoading = false;
          this.setLoadMoreLoading(false);
        }
      }

      replaceFromIncoming(incoming) {
        this.innerHTML = incoming.innerHTML;
        this.syncDataset(incoming);
        this.cacheElements();
        this.bindControls();
        this.syncLoadMoreLink();
      }

      appendFromIncoming(incoming) {
        const incomingGrid = incoming.querySelector('[data-product-grid]');

        if (!incomingGrid || !this.productGrid) return;

        const items = [...incomingGrid.children].filter(
          (child) => !child.hasAttribute('data-grid-highlight'),
        );

        if (items.length === 0) {
          this.hasMore = false;
          this.nextUrl = '';
          this.syncLoadMoreLink();
          this.syncPagination(incoming);
          this.updateLoadMoreVisibility();
          this.teardownAutoLoad();
          return;
        }

        const existingIds = new Set(
          [...this.productGrid.querySelectorAll('[data-product-id]')].map(
            (card) => card.dataset.productId,
          ),
        );

        const newItems = items.filter((item) => {
          const productId = item.dataset?.productId;

          if (!productId) return true;

          return !existingIds.has(productId);
        });

        if (newItems.length === 0) {
          this.hasMore = false;
          this.nextUrl = '';
          this.syncLoadMoreLink();
          this.syncPagination(incoming);
          this.updateLoadMoreVisibility();
          this.teardownAutoLoad();
          return;
        }

        newItems.forEach((item) => {
          this.productGrid.appendChild(item);
        });

        this.syncDataset(incoming);
        this.syncLoadMoreLink();
        this.syncPagination(incoming);
        this.updateLoadMoreVisibility();

        if (!this.hasMore) {
          this.teardownAutoLoad();
        }
      }

      syncDataset(incoming) {
        this.nextUrl = incoming.dataset.nextUrl || '';
        this.hasMore = incoming.dataset.hasMore === 'true';
        this.sortBy = incoming.dataset.sortBy || this.sortBy;
        this.dataset.loadMoreType = incoming.dataset.loadMoreType || this.loadMoreType;

        if (!this.nextUrl) {
          this.hasMore = false;
        }
      }

      syncLoadMoreLink() {
        if (!this.loadMoreButton) return;

        if (this.nextUrl) {
          this.loadMoreButton.setAttribute('href', this.nextUrl);
          this.loadMoreButton.removeAttribute('aria-disabled');
          this.loadMoreButton.removeAttribute('tabindex');
          return;
        }

        this.loadMoreButton.removeAttribute('href');
        this.loadMoreButton.setAttribute('aria-disabled', 'true');
        this.loadMoreButton.setAttribute('tabindex', '-1');
      }

      syncPagination(incoming) {
        const currentPagination = this.querySelector('[data-pagination]');
        const incomingPagination = incoming.querySelector('[data-pagination]');

        if (currentPagination && incomingPagination) {
          currentPagination.replaceWith(incomingPagination.cloneNode(true));
          return;
        }

        if (currentPagination && !incomingPagination) {
          currentPagination.remove();
          return;
        }

        if (!currentPagination && incomingPagination) {
          const insertAfter = this.loadMoreWrap || this.productGrid;

          insertAfter?.insertAdjacentElement('afterend', incomingPagination.cloneNode(true));
        }
      }

      cacheElements() {
        this.sortSelect = this.querySelector('[data-sort-select]');
        this.productGrid = this.querySelector('[data-product-grid]');
        this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
        this.loadMoreButton = this.querySelector('[data-load-more]');
      }

      bindControls() {
        this.sortSelect?.addEventListener('change', this.handleSortChange);
        this.loadMoreButton?.addEventListener('click', this.handleLoadMoreClick);
      }

      updateLoadMoreVisibility() {
        if (!this.loadMoreWrap) return;

        const shouldHide = !this.hasMore || this.loadMoreType === 'auto';

        this.loadMoreWrap.classList.toggle('product-grid__load-more--hidden', shouldHide);
      }

      setLoadMoreLoading(isLoading) {
        if (!this.loadMoreButton) return;

        this.loadMoreButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.loadMoreButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');

        if (isLoading) {
          this.loadMoreButton.setAttribute('tabindex', '-1');
          return;
        }

        if (this.hasMore && this.nextUrl) {
          this.loadMoreButton.removeAttribute('tabindex');
          this.loadMoreButton.removeAttribute('aria-disabled');
          return;
        }

        this.loadMoreButton.setAttribute('tabindex', '-1');
        this.loadMoreButton.setAttribute('aria-disabled', 'true');
      }

      handleQuickAddClick(event) {
        const button = event.target.closest('[data-quick-add]');

        if (!button || !this.contains(button)) return;

        event.preventDefault();

        const variantId = Number.parseInt(button.dataset.variantId || '', 10);

        if (!variantId || button.disabled) return;

        this.pendingVariantId = String(variantId);
        button.disabled = true;

        document.dispatchEvent(
          new CustomEvent('cart:add', {
            bubbles: true,
            detail: { variantId, quantity: 1, openDrawer: true },
          }),
        );
      }

      handleCartUpdated() {
        this.resetPendingQuickAdd();
      }

      handleCartError() {
        this.resetPendingQuickAdd();
      }

      resetPendingQuickAdd() {
        if (!this.pendingVariantId) return;

        const button = this.querySelector(
          `[data-quick-add][data-variant-id="${this.pendingVariantId}"]`,
        );

        if (button) {
          button.disabled = false;
        }

        this.pendingVariantId = null;
      }
    },
  );
}
